import React, { useState, useEffect } from 'react';
import { BicycleAddon, BicycleAddonFormData } from '../types/bicycle';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface BicycleAddonFormSliderProps {
  workspaceId: string;
  addonId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
}

const COLORS = [
  '#000000', // Black
  '#0000FF', // Blue
  '#00FF00', // Green
  '#00FFFF', // Cyan
  '#FFA500', // Orange
  '#FF00FF', // Magenta
  '#800080', // Purple
  '#FF0000', // Red
  '#FFFF00', // Yellow
];

const BicycleAddonFormSlider: React.FC<BicycleAddonFormSliderProps> = ({
  workspaceId,
  addonId,
  onSuccess,
  onCancel
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [initialProjectId, setInitialProjectId] = useState('');
  const [selectedColor, setSelectedColor] = useState('#FFA500'); // Orange as default
  const [isLimitedQuantity, setIsLimitedQuantity] = useState(false);
  const [quantity, setQuantity] = useState('');

  const initialFormData: BicycleAddonFormData = {
    projectId: initialProjectId,
    name: '',
    total: '0',
    available: '0',
    capable: '0',
    sold: '0',
    cost: '0',
    price: '0'
  };

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project if creating new addon
      if (!addonId && projectsList.length > 0) {
        setInitialProjectId(projectsList[0]._id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [workspaceId]);

  const validateForm = (data: BicycleAddonFormData): string | null => {
    if (!data.projectId) {
      return 'Project selection is required';
    }
    if (!data.name.trim()) {
      return 'Add-on name is required';
    }
    return null;
  };

  const transformFetchData = (response: any): BicycleAddonFormData => {
    const addon: BicycleAddon = response.bicycleAddon;

    // Set color from fetched data
    if (addon.color) {
      setSelectedColor(addon.color);
    }

    // Set limited quantity state from fetched data
    if (addon.total && addon.total > 0) {
      setIsLimitedQuantity(true);
      setQuantity(addon.total.toString());
    }

    return {
      projectId: addon.projectId || '',
      name: addon.name || '',
      total: addon.total?.toString() || '0',
      available: addon.available?.toString() || '0',
      capable: addon.capable?.toString() || '0',
      sold: addon.sold?.toString() || '0',
      cost: addon.cost?.toString() || '0',
      price: addon.price?.toString() || '0'
    };
  };

  const transformSubmitData = (data: BicycleAddonFormData) => {
    const totalQuantity = isLimitedQuantity && quantity ? parseInt(quantity) : 0;

    // When editing (addonId exists), preserve existing values for available, capable, and sold
    // When creating new, set available to total and others to 0
    const submitData: any = {
      projectId: data.projectId,
      name: data.name.trim(),
      color: selectedColor,
      limitedQuantity: isLimitedQuantity,
      total: totalQuantity,
      cost: data.cost ? parseFloat(data.cost) : 0,
      price: data.price ? parseFloat(data.price) : 0
    };

    // Only set these values when creating new addon
    if (!addonId) {
      submitData.available = totalQuantity;
      submitData.capable = 0;
      submitData.sold = 0;
    }

    return submitData;
  };

  if (!addonId && loadingProjects) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={initialProjectId}
      workspaceId={workspaceId}
      itemId={addonId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/bicycle-addons`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="bicycle-addon-form"
    >
      {({ formData, handleInputChange, loading }) => (
        <>
          {/* Project */}
          <div className="form-group">
            <label htmlFor="projectId" className="form-label">
              Project
            </label>
            {loadingProjects ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
              </div>
            ) : (
              <select
                id="projectId"
                name="projectId"
                className="form-input"
                value={formData.projectId}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Name */}
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>

          {/* Add-on Color */}
          <div className="form-group">
            <label className="form-label">
              Add-on Color
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: '15px', marginTop: '10px' }}>
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    border: selectedColor === color ? '4px solid #4CAF50' : '2px solid #D1D5DB',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s',
                    boxShadow: selectedColor === color ? '0 0 0 2px #fff, 0 0 0 4px #4CAF50' : 'none'
                  }}
                  disabled={loading}
                  title={color}
                >
                  {selectedColor === color && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Limited Quantity and Total Quantity - Same Row */}
          <div className="form-group">
            <div style={{ display: 'grid', gridTemplateColumns: isLimitedQuantity ? '1fr 1fr' : '1fr', gap: '16px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={isLimitedQuantity}
                    onChange={(e) => {
                      setIsLimitedQuantity(e.target.checked);
                      if (!e.target.checked) {
                        setQuantity('');
                      }
                    }}
                    disabled={loading}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span className="form-label" style={{ margin: 0 }}>Limited Quantity</span>
                </label>
              </div>

              {/* Quantity Input - shown when Limited Quantity is checked */}
              {isLimitedQuantity && (
                <div>
                  <label htmlFor="quantity" className="form-label">
                    Total Quantity:
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    className="form-input"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    disabled={loading}
                    min="1"
                    required
                    placeholder="Enter total quantity"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Cost and Price - Same Row */}
          <div className="form-group">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label htmlFor="cost" className="form-label">
                  Cost:
                </label>
                <input
                  type="number"
                  id="cost"
                  name="cost"
                  className="form-input"
                  value={formData.cost}
                  onChange={handleInputChange}
                  disabled={loading}
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label htmlFor="price" className="form-label">
                  Price:
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  className="form-input"
                  value={formData.price}
                  onChange={handleInputChange}
                  disabled={loading}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default BicycleAddonFormSlider;
